import { Collapse, List, Button, Tooltip, Switch } from "antd";
import { EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined, EyeOutlined } from "@ant-design/icons";
import { ReactComponent as InboxIcon } from "../../assets/images/inbox_customize.svg";
import { StyledButton } from "../../components/styles";
import { ItemListNameWrapper, ItemNameText, ItemNameWrapper, SelectedItem, StyledBody, StyledColumn, StyledHeader } from "../Category/styles";
import { EmailItemContent, EmailTemplateColorBox } from "./styles";
import { ActivationStatus } from "../../types";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { DragHandle } from "../PermissionKey/styles";

export const CollapseSection = ({ itemKey, label, activeKey, children }: any) => {
  return (
    <Collapse
      size="large"
      activeKey={activeKey}
      expandIcon={undefined}
      items={[
        {
          key: itemKey,
          label,
          children: <StyledBody>{children}</StyledBody>,
        },
      ]}
    />
  );
};

const ItemName: React.FC<{ className?: string; icon?: any; name: string }> = ({ className, icon, name }) => {
  return (
    <>
      <ItemNameWrapper className={className}>
        {icon && icon} <ItemNameText>{name}</ItemNameText>
      </ItemNameWrapper>
    </>
  );
};

interface ItemListProps {
  dataSource: any;
  loading: boolean;
  header?: any;
  onEdit: any;
  onDelete: any;
  onItemClick?: any;
  loadMore?: any;
  isSelected?: any;
  isSelectedItemOnClick?: any;
  onChange?: any;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hasViewPermission?: boolean;
  hasAddPermission?: boolean;
  handleDragEnd: (result: DropResult) => void;
}

export const ItemList: React.FC<ItemListProps> = ({ header, dataSource, loading, onEdit, onDelete, onItemClick, loadMore, isSelected, isSelectedItemOnClick, onChange, hasEditPermission, hasDeletePermission, hasViewPermission, hasAddPermission, handleDragEnd }) => {

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="droppable-list">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            <List
              className="email-template-list"
              loading={loading}
              dataSource={dataSource}
              {...(loadMore && { loadMore })}
              {...(header && { header })}
              renderItem={(item: any, index: number) => (
                <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <SelectedItem
                        isSelected={isSelected ? item.id === isSelected : false}
                        onClick={() => isSelectedItemOnClick && isSelectedItemOnClick(item)}
                      >
                        <List.Item
                          style={{ flexWrap: "nowrap" }}
                          actions={[
                            <EmailTemplateColorBox style={{ background: item?.color }} />,
                            hasEditPermission && (
                              <Switch
                                checkedChildren={<CheckOutlined />}
                                unCheckedChildren={<CloseOutlined />}
                                checked={item.status === ActivationStatus.Active}
                                onChange={() => onChange && onChange(item)}
                              />
                            ),
                            hasEditPermission && (
                              <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(item)} type="link" />
                            ),
                            hasDeletePermission && (
                              <Button size="small" icon={<DeleteOutlined />} onClick={() => onDelete(item)} type="link" danger />
                            ),
                            hasViewPermission && !hasAddPermission && !hasEditPermission && (
                              <Button size="small" icon={<EyeOutlined />} onClick={() => onEdit(item)} type="link" />
                            ),
                          ].filter(Boolean)}
                        >
                          <ItemListNameWrapper onClick={() => onItemClick && onItemClick()}>
                            <EmailItemContent>
                              <DragHandle>⋮⋮</DragHandle>
                              <Tooltip placement="topLeft" title={item?.longTitle}> <ItemName className="emailName" icon={item?.longTitle && <InboxIcon />} name={item?.longTitle} />  </Tooltip>
                            </EmailItemContent>
                          </ItemListNameWrapper>
                        </List.Item>
                      </SelectedItem>
                    </div>
                  )}
                </Draggable>
              )}
            />
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

interface ItemLabelProps {
  title: string;
  buttonLabel: string;
  onButtonClick?: any;
  onTitleClick?: any;
  hasAddPermission?: boolean;
}

export const ItemLabel: React.FC<ItemLabelProps> = ({ title, buttonLabel, onButtonClick, onTitleClick, hasAddPermission }) => (
  <StyledHeader onClick={() => onTitleClick && onTitleClick()}>
    <span>{title}</span>
    {hasAddPermission && <StyledButton
      type="primary"
      onClick={(e) => {
        onButtonClick && onButtonClick(e);
      }}
    >
      {buttonLabel}
    </StyledButton>}
  </StyledHeader>
);

interface ItemColumnProps {
  title: string;
  buttonLabel: string;
  onAddClick: () => void;
  dataSource: any;
  loading: boolean;
  header?: any;
  onEdit: any;
  onDelete: any;
  onItemClick?: any;
  loadMore?: any;
  isSelected?: any;
  isSelectedItemOnClick?: any;
  onChange?: any;
  hasAddPermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hasViewPermission?: boolean;
  handleDragEnd: (result: DropResult) => void;
}

export const ItemColumn: React.FC<ItemColumnProps> = ({ title, buttonLabel, onAddClick, dataSource, loading, isSelected, isSelectedItemOnClick, onEdit, onDelete, header, loadMore, onItemClick, onChange, hasAddPermission, hasEditPermission, hasDeletePermission, hasViewPermission, handleDragEnd }) => (
  <StyledColumn>
    <ItemLabel title={title} buttonLabel={buttonLabel} onButtonClick={onAddClick} hasAddPermission={hasAddPermission} />
    <StyledBody>
      <ItemList
        header={header}
        loading={loading}
        dataSource={dataSource}
        isSelected={isSelected}
        isSelectedItemOnClick={isSelectedItemOnClick}
        onEdit={onEdit}
        onDelete={onDelete}
        loadMore={loadMore}
        onItemClick={onItemClick}
        onChange={onChange}
        hasEditPermission={hasEditPermission}
        hasDeletePermission={hasDeletePermission}
        hasViewPermission={hasViewPermission}
        hasAddPermission={hasAddPermission}
        handleDragEnd={handleDragEnd}
      />
    </StyledBody>
  </StyledColumn>
);


// Single source of truth for all template variables, categorized
export const variableCategories = [
  { label: "Customer / User", vars: ["FIRSTNAME", "LASTNAME", "EMAIL", "PHONE", "PHONE2", "ADDRESS1", "ADDRESS2", "CITY", "STATE", "COUNTRY", "ZIP"] },
  { label: "Claim Details", vars: ["CLAIM_ID", "TRACKING_ID", "BOOKING_NUMBER", "LOST_DATE", "LOST_TIME_OF_DAY", "ITEM_REPORTEDDATETIME", "STATUS"] },
  { label: "Establishment & Location", vars: ["ESTABLISHMENT_TYPE_NAME", "ESTABLISHMENT_LOCATION_NAME", "ESTABLISHMENT_LOCATION_CODE", "LOST_LOCATION", "COMPANY_NAME"] },
  { label: "All Items (dynamic — handles 1 or many items with full Q&A)", vars: ["ALL_ITEMS_DETAILS"] },
  { label: "Item Categories (per-item, up to 5)", vars: ["ITEM_0_CATEGORY", "ITEM_1_CATEGORY", "ITEM_2_CATEGORY", "ITEM_3_CATEGORY", "ITEM_4_CATEGORY", "ITEM_0_SUBCATEGORY", "ITEM_1_SUBCATEGORY", "ITEM_2_SUBCATEGORY", "ITEM_3_SUBCATEGORY", "ITEM_4_SUBCATEGORY"] },
  { label: "Payment & Subscription", vars: ["PAYMENT_AMOUNT", "PAYMENT_DATE", "PAYMENT_ID", "PRICING_PLAN_NAME", "ACTIVATION_DATE", "EXPIRY_DATE"] },
  { label: "Links & System", vars: ["CLAIM_STATUS_LINK", "PAYMENT_REQUEST_LINK", "SITE_URL", "SUPPORT_EMAIL", "SIGNEDIN_USER"] },
];

// Flat list derived from categories — used by replaceDynamicVariables
export const dynamicVariables = variableCategories.flatMap((c) => c.vars);

// Question/Answer variables use the Q: prefix followed by the question's fieldName.
export const QUESTION_VARIABLE_PREFIX = "Q:";

export const replaceDynamicVariables = (emailBody: string, response: Record<string, any>) => {
  if (!emailBody || !response) return emailBody;

  // Step 1: Normalize HTML inside {{ }} — strip HTML tags from within variable placeholders
  // ReactQuill may split {{VAR}} across tags like {{<span>VAR</span>}} or <span>{{</span><span>VAR}}</span>
  // This regex finds {{ ... }} patterns that may contain HTML tags and strips tags from inside them
  emailBody = emailBody.replace(/\{\{([\s\S]*?)\}\}/g, (match, inner) => {
    const stripped = inner.replace(/<[^>]*>/g, '').trim();
    return `{{${stripped}}}`;
  });

  // Step 2: Replace standard variables
  dynamicVariables.forEach((variable) => {
    const value = response[variable];
    const replacement = value != null ? String(value) : "";
    emailBody = emailBody.replaceAll(`{{${variable}}}`, replacement);
  });

  // Step 3: Replace Q:FieldName variables from response
  Object.keys(response).forEach((key) => {
    if (key.startsWith(QUESTION_VARIABLE_PREFIX)) {
      const value = response[key];
      const replacement = value != null ? String(value) : "";
      emailBody = emailBody.replaceAll(`{{${key}}}`, replacement);
    }
  });

  // Step 4: Clean up any remaining unresolved {{...}} patterns
  emailBody = emailBody.replace(/\{\{[^}]*?\}\}/g, "");

  return emailBody;
}

export const CustomEmailTemplateTypes = {
  Normal: 1,
}